import isPlainObject from 'lodash/isPlainObject';
import toPairs from 'lodash/toPairs';
import camelCase from 'lodash/camelCase';

import getEnvironment from './environment';

const capitalize = word => `${word.slice(0, 1).toUpperCase()}${word.slice(1).toLowerCase()}`;

const camelize = (text, separator = '_') => {
  const words = text.split(separator);
  return [words[0], words.slice(1).map(word => capitalize(word)).join('')].join('');
};

export const toSnake = text => text.toLowerCase().replace(/ /g, '-');

// sets a defined list of properties from a source object to a target object
export const setNamedProperties = (
  targetObject, sourceObject, propertiesToSet, camelizePropertyNames = true
) => {
  if (typeof targetObject !== 'object' || typeof sourceObject !== 'object') {
    return;
  }
  (propertiesToSet || []).forEach((propertyName) => {
    const name = camelizePropertyNames ? camelize(propertyName) : propertyName;
    targetObject[name] = sourceObject[propertyName]; // eslint-disable-line no-param-reassign
  });
};

export const redirectTo = (href) => {
  if (getEnvironment() === 'development') {
    // eslint-disable-next-line no-console
    console.debug('Redirect (suppressed in development):', href);
    return;
  }
  window.location.assign(href);
};

export const queryStringToObject = (queryString) => {
  const obj = {};
  if (queryString) {
    decodeURI(queryString)
      .slice(1)
      .toLowerCase()
      .split('&')
      .forEach((item) => {
        const [k, v] = item.split('=');
        obj[k] = v || '';
      });
  }
  return obj;
};

export const reduceArrayToObject = (accumulator, element) => {
  if (!element) { throw new Error('Array element cannot be null.'); }
  if (element && element.id == null) { throw new Error('Array elements must have an id property.'); }

  accumulator[element.id] = element;
  return accumulator;
};

/*
 * Checks to see if a window object exists every interval until the timeout
 * ceiling is reached. Runs asynchronously.
 *
 * @param {string} name The name of the window object being waited for
 * @param {number} intervalMs The amount of time to wait between each interval
 * @param {number} timeoutMs The amount of time after which to time/error out
 * @returns {Promise} Resolves to the awaited object or rejects with an error
 */
export async function waitForWindowObject(name, intervalMs = 1000, timeoutMs = 5000) {
  return new Promise((resolve, reject) => {
    let timeWaited = 0;

    const intervalId = setInterval(() => {
      timeWaited += intervalMs;

      if (window[name] !== undefined) {
        clearInterval(intervalId);
        resolve(window[name]);
      }

      if (timeWaited >= timeoutMs) {
        clearInterval(intervalId);
        reject(new Error(`Timed out waiting for window.${name}`));
      }
    }, intervalMs);
  });
}

export function camelizeKeys(object, keysToSkip = []) {
  return toPairs(object)
    .filter(pair => !keysToSkip.includes(pair[0]))
    .reduce((acc, [key, value]) => {
      const newKey = camelCase(key);
      if (isPlainObject(value)) {
        return {
          ...acc,
          [newKey]: camelizeKeys(value)
        };
      }

      return {
        ...acc,
        [newKey]: value,
      };
    }, {});
}

export const noOp = () => {};
